import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listVideos, Video } from "../api";
import { formatBytes, relativeTime } from "../utils";

const INITIAL_PAGE = 10;
const NEXT_PAGE = 20;

type AvatarBadgeKind = "none" | "queued" | "running" | "partial" | "done";

interface AvatarBadgePreview {
  kind: AvatarBadgeKind;
  done: number;
  total: number;
}

// Preview-only: deterministic mock based on video_id so the screen looks
// realistic without any backend wiring. Will be replaced by a real
// aggregate read once batch sessions land on the backend.
function mockAvatarBadge(videoId: string): AvatarBadgePreview {
  const seed = videoId.charCodeAt(0) + videoId.charCodeAt(videoId.length - 1);
  const pick = seed % 5;
  if (pick === 0) return { kind: "none", done: 0, total: 0 };
  if (pick === 1) return { kind: "queued", done: 0, total: 4 };
  if (pick === 2) return { kind: "running", done: 1, total: 6 };
  if (pick === 3) return { kind: "partial", done: 3, total: 6 };
  return { kind: "done", done: 6, total: 6 };
}

function AvatarBadge({ preview }: { preview: AvatarBadgePreview }) {
  if (preview.kind === "none") return null;
  const map: Record<AvatarBadgeKind, { label: string; cls: string; dot?: string }> = {
    none: { label: "", cls: "" },
    queued: {
      label: "en attente",
      cls: "bg-stone/40 text-charcoal border-stone",
    },
    running: {
      label: "en cours",
      cls: "bg-lemonade/30 text-charcoal border-lemonade",
      dot: "animate-pulse bg-charcoal",
    },
    partial: {
      label: "partiel",
      cls: "bg-ivory-tint text-charcoal border-stone",
    },
    done: {
      label: "prêt",
      cls: "bg-charcoal text-ivory border-charcoal",
    },
  };
  const m = map[preview.kind];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10.5px] font-mono uppercase tracking-wide ${m.cls}`}
      title={`${preview.done}/${preview.total} avatars · ${m.label}`}
    >
      {m.dot && <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />}
      avatars {preview.done}/{preview.total}
    </span>
  );
}

// A row in the rendered list is either a standalone video (mono-block) or
// a group of parts that all came out of the same single recording (the
// multi-block split — source_video_id ties them together).
type Row =
  | { kind: "single"; video: Video }
  | { kind: "group"; sourceVideoId: string; parts: Video[] };

function groupByPartSource(videos: Video[]): Row[] {
  // Parts coming from the same _process call share an exact uploadedAt
  // timestamp, so a global desc sort already puts them adjacent. We walk
  // the list and bundle consecutive items sharing a source_video_id into
  // a single Row, then sort each group's parts by partIndex asc so the
  // visual order reads 1/3, 2/3, 3/3 from top to bottom.
  const rows: Row[] = [];
  let i = 0;
  while (i < videos.length) {
    const v = videos[i];
    if (v.source_video_id && v.part_index !== undefined) {
      const src = v.source_video_id;
      const parts: Video[] = [];
      let j = i;
      while (
        j < videos.length &&
        videos[j].source_video_id === src &&
        videos[j].part_index !== undefined
      ) {
        parts.push(videos[j]);
        j++;
      }
      parts.sort((a, b) => (a.part_index ?? 0) - (b.part_index ?? 0));
      rows.push({ kind: "group", sourceVideoId: src, parts });
      i = j;
    } else {
      rows.push({ kind: "single", video: v });
      i++;
    }
  }
  return rows;
}

export default function AdminVideos() {
  const nav = useNavigate();
  const [videos, setVideos] = useState<Video[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [titleFilter, setTitleFilter] = useState<string>("");
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listVideos(undefined, INITIAL_PAGE, 0)
      .then((items) => {
        setVideos(items);
        setHasMore(items.length === INITIAL_PAGE);
      })
      .catch((e) => {
        if (String(e.message).includes("401")) nav("/admin/login");
        else setErr(e.message);
      });
  }, [nav]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !videos) return;
    setLoadingMore(true);
    try {
      const items = await listVideos(undefined, NEXT_PAGE, videos.length);
      setVideos((prev) => [...(prev ?? []), ...items]);
      setHasMore(items.length === NEXT_PAGE);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, videos]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore, hasMore]);

  // Unique titles present in the result set → populate the filter dropdown
  const titles = useMemo(() => {
    if (!videos) return [] as string[];
    const s = new Set<string>();
    for (const v of videos) if (v.link_title) s.add(v.link_title);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [videos]);

  const filtered = useMemo(() => {
    if (!videos) return null;
    if (!titleFilter) return videos;
    return videos.filter((v) => v.link_title === titleFilter);
  }, [videos, titleFilter]);

  const rows = useMemo(
    () => (filtered ? groupByPartSource(filtered) : null),
    [filtered],
  );

  const openAvatarBuilder = (videoId: string) => {
    // Reuses the existing builder, pre-filled with this video as motion
    // source. The multi-select UI lives inside AvatarBuilderPanel.
    nav(`/admin?app=avatar&reuseVideoId=${encodeURIComponent(videoId)}`);
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Videos received</h2>
        {videos && (
          <span className="text-sm text-concrete">
            {filtered?.length} / {videos.length}
          </span>
        )}
      </div>

      {videos && videos.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-concrete">Filter by link:</label>
          <select
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            className="px-3 py-1.5 bg-white border border-stone rounded-lg text-sm focus:outline-none focus:border-charcoal"
          >
            <option value="">All ({videos.length})</option>
            {titles.map((t) => {
              const count = videos.filter((v) => v.link_title === t).length;
              return (
                <option key={t} value={t}>
                  {t} ({count})
                </option>
              );
            })}
          </select>
          {titleFilter && (
            <button
              onClick={() => setTitleFilter("")}
              className="text-xs text-concrete underline hover:text-charcoal"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {err && <div className="text-red-600">{err}</div>}
      {!videos && !err && <div className="text-concrete">Loading…</div>}

      {videos && videos.length === 0 && (
        <div className="text-concrete py-12 text-center bg-white rounded-xl">
          No videos yet. Create a link in the <b>New link</b> tab.
        </div>
      )}

      {filtered && filtered.length === 0 && videos && videos.length > 0 && (
        <div className="text-concrete py-8 text-center bg-white rounded-xl">
          No videos match <b>{titleFilter}</b>.
        </div>
      )}

      {rows && rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((row) =>
            row.kind === "group" ? (
              <li
                key={`group-${row.sourceVideoId}`}
                className="rounded-xl bg-white shadow-sm border-l-4 border-lemonade overflow-hidden"
              >
                <div className="px-4 py-2 bg-lemonade/15 border-b border-lemonade/40 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10.5px] font-mono uppercase tracking-wide text-charcoal/80">
                      Source unique
                    </span>
                    <span className="text-sm font-bold text-charcoal truncate">
                      {row.parts[0].link_title || "Untitled"}
                    </span>
                  </div>
                  <span className="text-[10.5px] font-mono text-charcoal/70">
                    {row.parts.length} vidéos · une même prise
                  </span>
                </div>
                <ul className="divide-y divide-stone">
                  {row.parts.map((v) => (
                    <VideoRow
                      key={v.video_id}
                      video={v}
                      onOpenAvatar={openAvatarBuilder}
                      inGroup
                    />
                  ))}
                </ul>
              </li>
            ) : (
              <li
                key={row.video.video_id}
                className="rounded-xl bg-white shadow-sm"
              >
                <ul>
                  <VideoRow
                    video={row.video}
                    onOpenAvatar={openAvatarBuilder}
                  />
                </ul>
              </li>
            ),
          )}
        </ul>
      )}

      {hasMore && rows && rows.length > 0 && (
        <div
          ref={sentinelRef}
          className="py-6 text-center text-xs text-concrete font-mono"
        >
          {loadingMore ? "Loading more…" : "Scroll for more"}
        </div>
      )}

      {rows && rows.length > 0 && (
        <p className="text-xs text-concrete font-mono">
          Preview UI · les badges « avatars X/Y » sont actuellement simulés (mock).
        </p>
      )}
    </section>
  );
}

function VideoRow({
  video: v,
  onOpenAvatar,
  inGroup = false,
}: {
  video: Video;
  onOpenAvatar: (videoId: string) => void;
  inGroup?: boolean;
}) {
  const badge = mockAvatarBadge(v.video_id);
  return (
    <li className="p-4 hover:bg-ivory transition">
      <div className="flex justify-between items-start gap-3">
        <Link
          to={`/admin/videos/${v.video_id}`}
          className="min-w-0 flex-1 block"
        >
          {!inGroup && (
            <div className="font-bold text-base truncate">
              {v.link_title || "Untitled"}
            </div>
          )}
          <div className="text-sm text-concrete mt-0.5">
            {relativeTime(v.uploaded_at)}
            {" · "}
            <span className="font-mono text-xs">
              {new Date(v.uploaded_at).toLocaleString()}
            </span>
            {" · "}
            {formatBytes(v.size_bytes)}
          </div>
          {v.recorder_name && (
            <div className="text-xs text-concrete/80 mt-0.5">
              from {v.recorder_name}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs text-concrete uppercase tracking-wider font-mono">
              {v.status}
            </span>
            {v.part_index !== undefined && v.total_parts && (
              <span className="inline-flex items-center px-2 py-0.5 rounded border border-lemonade/60 bg-lemonade/10 text-charcoal text-[10.5px] font-mono uppercase tracking-wide">
                Vidéo {v.part_index + 1}/{v.total_parts}
              </span>
            )}
            <AvatarBadge preview={badge} />
          </div>
        </Link>
        <div className="shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenAvatar(v.video_id);
            }}
            className="px-3 py-1.5 rounded-lg border border-charcoal text-charcoal text-xs font-mono uppercase tracking-wider hover:bg-charcoal hover:text-ivory transition"
            title="Lancer le builder avatar avec cette vidéo comme mouvement"
          >
            Générer avatar
          </button>
        </div>
      </div>
    </li>
  );
}
